import React, { useState } from 'react';
import {
  ArrowUpToLine, ArrowRightToLine, Move3d, RefreshCcw, Grid3x3, Axis3d,
  SlidersHorizontal, Layers3,
  Upload, Database, FlaskConical,
  ImageDown, FileDown, Clapperboard, History,
  LayoutGrid, Type, Maximize2,
} from 'lucide-react';
import { RibbonGroup, RibbonBtn, RibbonDivider } from './GisRibbon';
import { useTheme } from '../contexts/ThemeContext';
import { COLOR_MODES } from '../hooks/useViewer3dView';
import { TURNTABLE_DURATIONS } from '../lib/turntable';

// ─── Display modes for ribbon buttons (shared look/feel with the 2D ribbon) ───
const DISPLAY_MODES = [
  { id: 'both', label: 'Icon + Name', icon: LayoutGrid },
  { id: 'icon', label: 'Icon Only', icon: Maximize2 },
  { id: 'name', label: 'Name Only', icon: Type },
];

// ─── Tab definitions ───
const TABS = [
  { id: 'view', label: 'View' },
  { id: 'volume', label: 'Volume' },
  { id: 'insert', label: 'Insert' },
  { id: 'reports', label: 'Reports' },
  { id: 'share', label: 'Share' },
];

// ─── Ribbon content per tab ───
function ViewRibbon({ onCameraPreset, onResetSavedView, colorMode, setColorMode, showGrid, onToggleGrid, showAxes, onToggleAxes, displayMode }) {
  const { C } = useTheme();
  return (
    <div className="flex items-center gap-1 px-3 h-[38px] overflow-x-auto">
      <RibbonGroup label="Camera">
        <RibbonBtn icon={ArrowUpToLine} label="Top" onClick={() => onCameraPreset('top')} displayMode={displayMode} />
        <RibbonBtn icon={ArrowRightToLine} label="Side" onClick={() => onCameraPreset('side')} displayMode={displayMode} />
        <RibbonBtn icon={Move3d} label="Perspective" onClick={() => onCameraPreset('perspective')} displayMode={displayMode} />
        <RibbonBtn icon={RefreshCcw} label="Reset View" onClick={() => onCameraPreset('reset')} displayMode={displayMode} />
        {onResetSavedView && (
          <RibbonBtn icon={History} label="Reset saved view" onClick={onResetSavedView} displayMode={displayMode}
            tooltip="Forget this project's saved 3D view and return everything to the defaults" />
        )}
      </RibbonGroup>

      <RibbonDivider />

      <RibbonGroup label="Color Mode">
        <select value={colorMode} onChange={e => setColorMode(e.target.value)}
          className="h-7 px-2 text-[11px] rounded-md outline-none"
          style={{ background: C.bg4, border: `1px solid ${C.border}`, color: C.text0 }}>
          {COLOR_MODES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
      </RibbonGroup>

      <RibbonDivider />

      <RibbonGroup label="Scene">
        <RibbonBtn icon={Grid3x3} label="Grid" active={showGrid} onClick={onToggleGrid} tone="amber" displayMode={displayMode} />
        <RibbonBtn icon={Axis3d} label="Axes" active={showAxes} onClick={onToggleAxes} tone="amber" displayMode={displayMode} />
      </RibbonGroup>
    </div>
  );
}

function VolumeRibbon({ showVolumePanel, onToggleVolumePanel, onResetVolumeFilters, showFences, onToggleFences, displayMode }) {
  return (
    <div className="flex items-center gap-1 px-3 h-[38px] overflow-x-auto">
      <RibbonGroup label="Panel">
        <RibbonBtn icon={SlidersHorizontal} label="Volume Panel" active={showVolumePanel} onClick={onToggleVolumePanel} displayMode={displayMode} size="lg" />
      </RibbonGroup>

      <RibbonDivider />

      <RibbonGroup label="Filters">
        <RibbonBtn icon={RefreshCcw} label="Reset Filters" onClick={onResetVolumeFilters} displayMode={displayMode} />
        <RibbonBtn icon={Layers3} label="Fence Diagrams" active={showFences} onClick={onToggleFences} displayMode={displayMode} />
      </RibbonGroup>
    </div>
  );
}

function InsertRibbon({ onImportInversionModel, onLoadSample, sampleBusy, onAddData, displayMode }) {
  return (
    <div className="flex items-center gap-1 px-3 h-[38px] overflow-x-auto">
      <RibbonGroup label="Data">
        <RibbonBtn icon={Upload} label="Import Inversion Model" onClick={onImportInversionModel} displayMode={displayMode} size="lg" />
        {onLoadSample && (
          <RibbonBtn icon={FlaskConical} label="Load sample" onClick={onLoadSample} disabled={sampleBusy} displayMode={displayMode} size="lg"
            tooltip={sampleBusy ? 'Loading the sample…' : 'EarthImager 3D demo: hill3d trial 1 inverted volume'} />
        )}
        <RibbonBtn icon={Database} label="Add Data" onClick={onAddData} displayMode={displayMode} size="lg" />
      </RibbonGroup>
    </div>
  );
}

function TurntableControls({ turntable, displayMode }) {
  const { C } = useTheme();
  const [duration, setDuration] = useState(12);
  if (!turntable) return null;
  if (!turntable.supported) {
    return <span className="text-[10px] px-1" style={{ color: C.text3 }} data-testid="turntable-unsupported">Turntable video needs MediaRecorder (not available in this browser)</span>;
  }
  if (turntable.recording) {
    return (
      <>
        <span className="text-[10px] px-1" style={{ color: C.text1 }} data-testid="turntable-progress">
          Recording {Math.floor(turntable.elapsed)} s / {turntable.duration} s
        </span>
        <RibbonBtn icon={Clapperboard} label="Cancel" onClick={turntable.cancel} displayMode={displayMode} />
      </>
    );
  }
  return (
    <>
      <RibbonBtn icon={Clapperboard} label="Turntable video" onClick={() => turntable.start(duration)} displayMode={displayMode} size="lg"
        tooltip="Record one 360 degree turn of the scene as a video (MP4, or WebM where the browser cannot record MP4)" />
      <select value={duration} onChange={(e) => setDuration(Number(e.target.value))} aria-label="Turntable duration"
        className="h-7 px-1 text-[11px] rounded-md outline-none"
        style={{ background: C.bg4, border: `1px solid ${C.border}`, color: C.text0 }}>
        {TURNTABLE_DURATIONS.map((d) => <option key={d} value={d}>{d} s</option>)}
      </select>
      {turntable.error && <span role="alert" className="text-[10px] px-1" style={{ color: '#f87171' }}>{turntable.error}</span>}
    </>
  );
}

function ReportsRibbon({ onExportImage, turntable, displayMode }) {
  return (
    <div className="flex items-center gap-1 px-3 h-[38px] overflow-x-auto">
      <RibbonGroup label="Export">
        <RibbonBtn icon={ImageDown} label="Export Image" onClick={onExportImage} displayMode={displayMode} size="lg" />
        <TurntableControls turntable={turntable} displayMode={displayMode} />
      </RibbonGroup>
    </div>
  );
}

function ShareRibbon({ onExportModel, displayMode }) {
  return (
    <div className="flex items-center gap-1 px-3 h-[38px] overflow-x-auto">
      <RibbonGroup label="Export">
        <RibbonBtn icon={FileDown} label="Export Model" onClick={onExportModel} displayMode={displayMode} size="lg" />
      </RibbonGroup>
    </div>
  );
}

// ─── Main Ribbon Component ───
export default function Viewer3DRibbon(props) {
  const { C } = useTheme();
  const [activeTab, setActiveTab] = useState('view');
  const [displayMode, setDisplayMode] = useState(() => localStorage.getItem('ribbon-display') || 'both');

  const cycleDisplay = () => {
    const modes = ['both', 'icon', 'name'];
    const next = modes[(modes.indexOf(displayMode) + 1) % modes.length];
    setDisplayMode(next);
    localStorage.setItem('ribbon-display', next);
  };

  const CurrentModeIcon = DISPLAY_MODES.find(m => m.id === displayMode)?.icon || LayoutGrid;

  const renderContent = () => {
    switch (activeTab) {
      case 'view': return <ViewRibbon {...props} displayMode={displayMode} />;
      case 'volume': return <VolumeRibbon {...props} displayMode={displayMode} />;
      case 'insert': return <InsertRibbon {...props} displayMode={displayMode} />;
      case 'reports': return <ReportsRibbon {...props} displayMode={displayMode} />;
      case 'share': return <ShareRibbon {...props} displayMode={displayMode} />;
      default: return null;
    }
  };

  return (
    <div className="flex flex-col" style={{ background: C.bg1, borderBottom: `1px solid ${C.border}` }}>
      {/* Tab bar */}
      <div className="flex items-center h-8 px-2 gap-0" style={{ borderBottom: `1px solid ${C.border}` }}>
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 h-full text-[12px] font-medium border-b-2 transition-colors cursor-pointer
              ${activeTab === tab.id
                ? 'text-accent border-accent'
                : 'border-transparent hover:text-slate-200 hover:border-slate-500'}`}
            style={activeTab === tab.id ? undefined : { color: C.text3 }}
          >
            {tab.label}
          </button>
        ))}

        {/* Display mode toggle (right-aligned) */}
        <div className="ml-auto flex items-center gap-1">
          <button
            onClick={cycleDisplay}
            title={`Display: ${DISPLAY_MODES.find(m => m.id === displayMode)?.label} — click to cycle`}
            className="flex items-center gap-1 px-2 h-6 rounded text-[10px] hover:text-slate-300 hover:bg-white/5 transition-colors cursor-pointer border border-transparent hover:border-white/10"
            style={{ color: C.text3 }}
          >
            <CurrentModeIcon size={12} />
            <span className="hidden sm:inline">{DISPLAY_MODES.find(m => m.id === displayMode)?.label}</span>
          </button>
        </div>
      </div>

      {/* Ribbon content */}
      {renderContent()}
    </div>
  );
}
